import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

/**
 * Directive to allow decimals with length validation and formatting
 * Replicates AngularJS 'only-digits' + 'format-dec-num' + 'decimal-digits' behavior
 * 
 * Features:
 * - Accepts digits 0-9, minus sign (-), and decimal point (.)
 * - Configurable decimal places (default: 4)
 * - Max length: 15 digits for integer part
 * - Formats numbers with thousand separators during input
 * - Returns clean number (without separators) to model
 * 
 * Usage:
 *   <input onlyDigitsDecimalWithLength [decimalDigits]="4">
 */
@Directive({
    selector: '[onlyDigitsDecimalWithLength]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OnlyDigitsDecimalWithLengthDirective),
            multi: true
        }
    ],
    standalone: false
})
export class OnlyDigitsDecimalWithLengthDirective implements ControlValueAccessor {
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  /** Number of decimal places allowed (default: 4) */
  @Input() decimalDigits: number = 4;

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if (value) {
      // Remove all characters except digits, minus sign, and decimal point
      let cleaned = value.replace(/[^0-9.-]/g, '');

      // Handle multiple minus signs - keep only first one and only at start
      if (cleaned.includes('-')) {
        const hasMinusAtStart = cleaned.startsWith('-');
        cleaned = cleaned.replace(/-/g, '');
        if (hasMinusAtStart) {
          cleaned = '-' + cleaned;
        }
      }

      // Handle multiple decimal points - keep only first one
      const dotIndex = cleaned.indexOf('.');
      if (dotIndex !== -1) {
        const beforeDot = cleaned.substring(0, dotIndex + 1);
        const afterDot = cleaned.substring(dotIndex + 1).replace(/\./g, '');
        cleaned = beforeDot + afterDot;
      }

      // Split into integer and decimal parts
      const isNegative = cleaned.startsWith('-');
      const numericPart = isNegative ? cleaned.substring(1) : cleaned;
      const parts = numericPart.split('.');
      let integerPart = parts[0] || '';
      let decimalPart = parts.length > 1 ? parts[1] : '';

      // Limit integer part to 15 digits
      if (integerPart.length > 15) {
        integerPart = integerPart.substring(0, 15);
      }

      // Limit decimal part to specified decimal digits
      if (decimalPart.length > this.decimalDigits) {
        decimalPart = decimalPart.substring(0, this.decimalDigits);
      }

      // Format integer part with thousand separators
      const formattedInteger = this.formatNumber(integerPart);

      // Build final display value
      let displayValue = isNegative ? '-' : '';
      displayValue += formattedInteger;
      if (parts.length > 1) {
        displayValue += '.' + decimalPart;
      }

      input.value = displayValue;

      // Build clean value for model (without separators)
      let cleanValue = isNegative ? '-' : '';
      cleanValue += integerPart;
      if (parts.length > 1) {
        cleanValue += '.' + decimalPart;
      }

      // Convert to number or null
      const numericValue = cleanValue === '' || cleanValue === '-' || cleanValue === '.' || cleanValue === '-.' 
        ? null 
        : parseFloat(cleanValue);
      
      this.onChange(numericValue);
    } else {
      this.onChange(null);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    
    // On blur, format with full decimal places if there's a decimal
    const input = this.el.nativeElement as HTMLInputElement;
    const value = input.value;
    
    if (value && value !== '-') {
      // Re-read and format properly on blur
      const cleanValue = value.replace(/[^0-9.-]/g, '');
      const numVal = parseFloat(cleanValue);
      
      if (!isNaN(numVal)) {
        // Format with proper decimal places
        const formatted = this.formatWithDecimals(numVal);
        input.value = formatted;
      }
    }
  }

  /**
   * Format number with thousand separators
   * Example: 1234567 -> 1,234,567
   */
  private formatNumber(value: string): string {
    if (!value || value === '') return '';
    
    // Remove any existing commas
    const cleanValue = value.replace(/,/g, '');
    
    // Add thousand separators
    return cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Format number with thousand separators and decimal places
   */
  private formatWithDecimals(value: number): string {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    // Split into integer and decimal parts
    const [intPart, decPart] = absValue.toString().split('.');
    
    // Format integer part with thousand separators
    const formattedInt = this.formatNumber(intPart);
    
    // Build result
    let result = isNegative ? '-' : '';
    result += formattedInt;
    
    if (decPart !== undefined) {
      result += '.' + decPart;
    }
    
    return result;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    const input = this.el.nativeElement as HTMLInputElement;
    if (value !== null && value !== undefined) {
      // Format the value for display
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (!isNaN(numValue)) {
        input.value = this.formatWithDecimals(numValue);
      } else {
        input.value = '';
      }
    } else {
      input.value = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    const input = this.el.nativeElement as HTMLInputElement;
    input.disabled = isDisabled;
  }
}
